import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Phone, MapPin, Github, Linkedin, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

export const ContactSection = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const contactInfo = [
    {
      icon: Mail,
      label: t('contact.info.email'),
      value: 'eminiftiyev23@gmail.com',
      href: 'mailto:eminiftiyev23@gmail.com',
    },
    {
      icon: Phone,
      label: t('contact.info.phone'),
      value: '+994 99 399 19 13',
      href: 'tel:+994993991913',
    },
    {
      icon: MapPin,
      label: t('contact.info.location'),
      value: 'Baku, Azerbaijan',
      href: null,
    },
  ];

  const socialLinks = [
    {
      icon: Github,
      href: 'https://github.com/eminniftili',
      label: 'GitHub',
    },
    {
      icon: Linkedin,
      href: 'https://www.linkedin.com/in/eminniftili',
      label: 'LinkedIn',
    },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('contact.form.name_required');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contact.form.email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.form.email_invalid');
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contact.form.message_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission (mock)
    setTimeout(() => {
      toast.success(t('contact.form.success'));
      setFormData({ name: '', email: '', subject: '', message: '' });
      setErrors({});
      setIsSubmitting(false);
    }, 1000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      ref={ref}
      className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 bg-[#0A0E27]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#F8FAFC] mb-4">
            {t('contact.title')}
          </h2>
          <p className="text-[#CBD5E1] text-lg md:text-xl">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <Card className="bg-[#1A1F3A] rounded-2xl p-6 md:p-8 border border-[#22D3EE]/20">
              <h3 className="text-2xl font-semibold text-[#F8FAFC] mb-6">
                {t('contact.info.title')}
              </h3>

              <div className="space-y-4">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  const content = (
                    <div className="flex items-start space-x-4 p-4 rounded-lg bg-[#0F172A] border border-[#22D3EE]/20 hover:border-[#22D3EE]/40 transition-colors duration-300">
                      <div className="flex-shrink-0 p-2 rounded-lg bg-[#22D3EE]/10">
                        <Icon className="w-5 h-5 text-[#22D3EE]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-[#94A3B8] mb-1">{info.label}</p>
                        <p className="text-[#F8FAFC] font-medium">{info.value}</p>
                      </div>
                    </div>
                  );

                  return info.href ? (
                    <a
                      key={index}
                      href={info.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {content}
                    </a>
                  ) : (
                    <div key={index}>{content}</div>
                  );
                })}
              </div>

              <div className="mt-6 pt-6 border-t border-[#22D3EE]/20">
                <p className="text-sm text-[#94A3B8] mb-4">{t('contact.info.social')}</p>
                <div className="flex space-x-3">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 rounded-full bg-[#0F172A] border border-[#22D3EE]/30 text-[#22D3EE] hover:bg-[#22D3EE] hover:text-[#0A0E27] hover:shadow-[0_0_20px_rgba(34,211,238,0.3)] transition-all duration-300"
                        aria-label={social.label}
                      >
                        <Icon className="w-5 h-5" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Card className="bg-[#1A1F3A] rounded-2xl p-6 md:p-8 border border-[#22D3EE]/20">
              <h3 className="text-2xl font-semibold text-[#F8FAFC] mb-6">
                {t('contact.form.title')}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.form.name')}
                    data-testid="form-input"
                    className="w-full px-4 py-3 rounded-lg bg-[#0F172A] border border-[#22D3EE]/30 text-[#F8FAFC] placeholder:text-[#94A3B8] focus:border-[#22D3EE] focus:ring-2 focus:ring-[#22D3EE]/20 focus:outline-none transition-all duration-200"
                  />
                  {errors.name && (
                    <p className="text-sm text-[#EF4444] mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.form.email')}
                    data-testid="form-input"
                    className="w-full px-4 py-3 rounded-lg bg-[#0F172A] border border-[#22D3EE]/30 text-[#F8FAFC] placeholder:text-[#94A3B8] focus:border-[#22D3EE] focus:ring-2 focus:ring-[#22D3EE]/20 focus:outline-none transition-all duration-200"
                  />
                  {errors.email && (
                    <p className="text-sm text-[#EF4444] mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={t('contact.form.subject')}
                    data-testid="form-input"
                    className="w-full px-4 py-3 rounded-lg bg-[#0F172A] border border-[#22D3EE]/30 text-[#F8FAFC] placeholder:text-[#94A3B8] focus:border-[#22D3EE] focus:ring-2 focus:ring-[#22D3EE]/20 focus:outline-none transition-all duration-200"
                  />
                </div>

                <div>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.form.message')}
                    data-testid="form-input"
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg bg-[#0F172A] border border-[#22D3EE]/30 text-[#F8FAFC] placeholder:text-[#94A3B8] focus:border-[#22D3EE] focus:ring-2 focus:ring-[#22D3EE]/20 focus:outline-none transition-all duration-200 min-h-[120px] resize-y"
                  />
                  {errors.message && (
                    <p className="text-sm text-[#EF4444] mt-1">{errors.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-form-submit"
                  className="w-full px-8 py-4 rounded-full font-semibold text-base bg-gradient-to-r from-[#22D3EE] to-[#14B8A6] text-[#0A0E27] hover:shadow-[0_0_30px_rgba(34,211,238,0.5)] hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? (
                    <>{t('contact.form.sending')}</>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {t('contact.form.send')}
                    </>
                  )}
                </Button>
              </form>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
